import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../api/api";
import "../../style/userDetails.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString();
const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
const fmtDateTime = (d) =>
  new Date(d).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accentClass }) => (
  <div className="stat-card">
    <span className="stat-label">{label}</span>
    <span className={`stat-value ${accentClass || ""}`}>{value}</span>
    {sub && <span className="stat-sub">{sub}</span>}
  </div>
);

const InfoRow = ({ label, children }) => (
  <div className="info-row">
    <span className="info-label">{label}</span>
    <span className="info-value">{children}</span>
  </div>
);

const EmptyState = ({ cols, msg }) => (
  <tr>
    <td colSpan={cols} className="empty-row">
      — {msg} —
    </td>
  </tr>
);

const Badge = ({ color, children }) => {
  const badgeClass = `badge badge-${color}`;
  return <span className={badgeClass}>{children}</span>;
};

const ProgressBar = ({ percentage }) => {
  let fillClass = "progress-fill progress-fill-low";
  if (percentage > 80) {
    fillClass = "progress-fill progress-fill-high";
  } else if (percentage > 50) {
    fillClass = "progress-fill progress-fill-medium";
  }

  return (
    <div className="progress-wrap">
      <div className="progress-labels">
        <span>0%</span>
        <span>Used: {percentage.toFixed(1)}%</span>
        <span>100%</span>
      </div>
      <div className="progress-bar">
        <div className={fillClass} style={{ width: `${Math.min(percentage, 100)}%` }} />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const UserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("financial");
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get(`/admin/users/${userId}`);
        setData(res.data);
      } catch (err) {
        console.error("User details error:", err);
        setError("Failed to load user details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  // ── Loading State ──────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="loading-wrap">
        <div className="spinner" />
        Loading user details…
      </div>
    );

  if (error || !data)
    return (
      <div className={`loading-wrap ${error ? "error-state" : ""}`}>
        {error || "No data found."}
      </div>
    );

  const {
    user,
    creditSummary = {},
    subscriptionSummary = {},
    financialSummary = {},
    usageSummary = {},
    subscriptions = [],
    financialTransactions = [],
    usageLogs = [],
  } = data;

  const utilPct = parseFloat(creditSummary.utilizationRate) || 0;

  return (
    <div className="user-details-root">
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="top-bar">
        <button className="back-btn" onClick={() => navigate("/admin/users")}>
          ← Back
        </button>
        <h2 className="page-title">User Details</h2>
        <Badge color={user.isBlocked ? "red" : "green"}>
          {user.isBlocked ? "Blocked" : "Active"}
        </Badge>
      </div>

      {/* ── Summary stat cards ───────────────────────────────────────────────── */}
      <div className="grid-4">
        <StatCard
          label="Current Balance"
          value={fmt(creditSummary.currentBalance)}
          sub="credits available"
          accentClass="stat-value-accent-blue"
        />
        <StatCard
          label="Total Purchased"
          value={fmt(creditSummary.totalPurchased)}
          sub={`₹${fmt(financialSummary.totalAdded)} added`}
          accentClass="stat-value-accent-green"
        />
        <StatCard
          label="Total Used"
          value={fmt(creditSummary.totalUsed)}
          sub={`${usageSummary.totalSessions} sessions`}
          accentClass="stat-value-accent-pink"
        />
        <StatCard
          label="Avg / Session"
          value={usageSummary.avgCreditsPerSession}
          sub="credits per chat"
          accentClass="stat-value-accent-orange"
        />
      </div>

      {/* ── Profile + Credit utilization ────────────────────────────────────── */}
      <div className="grid-2">
        {/* Profile card */}
        <div className="card">
          <p className="card-title">Profile</p>
          <InfoRow label="Name">{user.name}</InfoRow>
          <InfoRow label="Email">
            <span className="info-value-accent">{user.email}</span>
          </InfoRow>
          <InfoRow label="Role">
            <Badge color={user.role === "admin" ? "yellow" : "gray"}>
              {user.role}
            </Badge>
          </InfoRow>
          <InfoRow label="Joined">{fmtDate(user.createdAt)}</InfoRow>
          <InfoRow label="Last Updated">{fmtDate(user.updatedAt)}</InfoRow>

          {/* Departments */}
          <div className="departments-container">
            <p className="card-title">Departments</p>
            {user.departments?.length > 0 ? (
              user.departments.map((d) => (
                <span key={d._id} className="tag">
                  {d.name}
                </span>
              ))
            ) : (
              <span className="departments-none">None assigned</span>
            )}
          </div>
        </div>

        {/* Credit utilization */}
        <div className="card">
          <p className="card-title">Credit Utilization</p>
          <InfoRow label="Purchased">{fmt(creditSummary.totalPurchased)}</InfoRow>
          <InfoRow label="Used">{fmt(creditSummary.totalUsed)}</InfoRow>
          <InfoRow label="Remaining">{fmt(creditSummary.currentBalance)}</InfoRow>
          <InfoRow label="Utilization Rate">
            <span
              className={
                utilPct > 80
                  ? "utilization-text-high font-strong"
                  : utilPct > 50
                  ? "utilization-text-medium font-strong"
                  : "utilization-text-low font-strong"
              }
            >
              {creditSummary.utilizationRate}
            </span>
          </InfoRow>

          <ProgressBar percentage={utilPct} />

          <div style={{ marginTop: "20px" }}>
            <p className="card-title">Subscriptions</p>
            <InfoRow label="Total">{subscriptionSummary.total}</InfoRow>
            <InfoRow label="Active">
              <Badge color="green">{subscriptionSummary.active}</Badge>
            </InfoRow>
            <InfoRow label="Failed">
              <Badge color="red">{subscriptionSummary.failed}</Badge>
            </InfoRow>
            <InfoRow label="Total Spent">₹{fmt(subscriptionSummary.totalSpent)}</InfoRow>
          </div>
        </div>
      </div>

      {/* ── Subscriptions table ──────────────────────────────────────────────── */}
      <div className="card card-overflow">
        <p className="section-title">Subscriptions</p>
        <table className="table">
          <thead>
            <tr>
              {["Plan", "Credits", "Amount Paid", "Status", "Date"].map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subscriptions.length === 0 ? (
              <EmptyState cols={5} msg="No subscriptions found" />
            ) : (
              subscriptions.map((s) => (
                <tr key={s._id}>
                  <td className="font-bold">{s.planName}</td>
                  <td>{fmt(s.credits)}</td>
                  <td>₹{fmt(s.amountPaid)}</td>
                  <td>
                    <Badge
                      color={
                        s.paymentStatus === "paid"
                          ? "green"
                          : s.paymentStatus === "failed"
                          ? "red"
                          : "yellow"
                      }
                    >
                      {s.paymentStatus}
                    </Badge>
                  </td>
                  <td>{fmtDate(s.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <div className="tab-row">
        {[
          { key: "financial", label: `Financial (${financialTransactions.length})` },
          { key: "usage", label: `Usage Logs (${usageLogs.length})` },
        ].map((t) => (
          <button
            key={t.key}
            className={`tab-button ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Financial tab ───────────────────────────────────────────────────── */}
      {activeTab === "financial" && (
        <div className="card card-overflow">
          {/* Mini summary bar */}
          <div className="summary-bar">
            <div className="summary-item">
              <div className="stat-label">Total Added</div>
              <div className="summary-value summary-value-green">
                +{fmt(financialSummary.totalAdded)}
              </div>
            </div>
            <div className="summary-item">
              <div className="stat-label">Total Deducted</div>
              <div className="summary-value summary-value-red">
                -{fmt(financialSummary.totalDeducted)}
              </div>
            </div>
            <div className="summary-item">
              <div className="stat-label">Transactions</div>
              <div className="summary-value summary-value-white">
                {financialSummary.totalTransactions}
              </div>
            </div>
            {financialSummary.lastTransaction && (
              <div className="summary-item">
                <div className="stat-label">Last Transaction</div>
                <div className="summary-value-small">
                  {fmtDateTime(financialSummary.lastTransaction.createdAt)}
                </div>
              </div>
            )}
          </div>

          <table className="table">
            <thead>
              <tr>
                {["Amount", "Type", "Reason", "Balance After", "Date & Time"].map(
                  (h) => (
                    <th key={h}>{h}</th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {financialTransactions.length === 0 ? (
                <EmptyState cols={5} msg="No financial transactions" />
              ) : (
                financialTransactions.map((t) => (
                  <tr key={t._id}>
                    <td className="font-bold">
                      <span
                        className={
                          t.type === "credit" ? "text-credit" : "text-debit"
                        }
                      >
                        {t.type === "credit" ? "+" : "-"}
                        {fmt(t.amount)}
                      </span>
                    </td>
                    <td>
                      <Badge color={t.type === "credit" ? "green" : "red"}>
                        {t.type}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-accent text-capitalize">
                        {t.reason}
                      </span>
                    </td>
                    <td className="text-dim">{fmt(t.balanceAfter)}</td>
                    <td className="text-dimmer">{fmtDateTime(t.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Usage tab ───────────────────────────────────────────────────────── */}
      {activeTab === "usage" && (
        <div className="card card-overflow">
          {/* Mini summary bar */}
          <div className="summary-bar">
            <div className="summary-item">
              <div className="stat-label">Total Sessions</div>
              <div className="summary-value summary-value-white">
                {usageSummary.totalSessions}
              </div>
            </div>
            <div className="summary-item">
              <div className="stat-label">Credits Consumed</div>
              <div className="summary-value summary-value-pink">
                {fmt(usageSummary.totalCreditsUsed)}
              </div>
            </div>
            <div className="summary-item">
              <div className="stat-label">Avg / Session</div>
              <div className="summary-value summary-value-orange">
                {usageSummary.avgCreditsPerSession}
              </div>
            </div>
            {usageSummary.lastUsed && (
              <div className="summary-item">
                <div className="stat-label">Last Active</div>
                <div className="summary-value-small">
                  {fmtDateTime(usageSummary.lastUsed)}
                </div>
              </div>
            )}
          </div>

          <table className="table">
            <thead>
              <tr>
                {["Action", "Credits Used", "Balance After", "Date & Time"].map(
                  (h) => (
                    <th key={h}>{h}</th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {usageLogs.length === 0 ? (
                <EmptyState cols={4} msg="No usage logs" />
              ) : (
                usageLogs.map((u) => (
                  <tr key={u._id}>
                    <td>
                      <span className="text-accent">Chat Request</span>
                    </td>
                    <td className="text-debit font-bold">-{fmt(u.amount)}</td>
                    <td className="text-dim">{fmt(u.balanceAfter)}</td>
                    <td className="text-dimmer">{fmtDateTime(u.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserDetails;